import React, { useState, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, ArrowLeft } from 'lucide-react';
import './ColumbariumGrid.css';

const ColumbariumGrid = ({ onBackToMap, statusFilter = 'all', onFloorChange, onFilterChange }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentFloor, setCurrentFloor] = useState(1);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });


  // Generate 8x50 grid with different statuses and spacing every 10 columns
  const generateGrid = (floor = currentFloor) => {
    const grid = [];
    const statuses = ['available', 'occupied', 'reserved', 'unavailable'];
    const statusColors = {
      available: '#22c55e',    // Green
      occupied: '#ef4444',     // Red
      reserved: '#3b82f6',     // Blue
      unavailable: '#f59e0b'   // Yellow/Orange
    };

    // Create a more random distribution using a better hash function
    // Include floor in hash to make different floors have different patterns
    const hash = (x, y, floorNum) => {
      let h = x * 374761393 + y * 668265263 + floorNum * 123456789;
      h = (h ^ (h >> 13)) * 1274126177;
      return (h ^ (h >> 16)) % 100;
    };

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 54; col++) { // 50 data columns + 4 spacer columns
        // Check if this is a spacer column (every 11th column starting from 10)
        if (col === 10 || col === 21 || col === 32 || col === 43) {
          grid.push({
            id: `spacer-${row}-${col}`,
            row,
            col,
            status: 'spacer',
            color: 'white',
            slotId: 'SPACER'
          });
        } else {
          // Calculate the actual data column index (accounting for spacers)
          const dataCol = col > 43 ? col - 4 : col > 32 ? col - 3 : col > 21 ? col - 2 : col > 10 ? col - 1 : col;
          
          // Use a more complex hash for better randomness
          const randomValue = hash(row, dataCol, floor);
          let status;
          
          if (randomValue < 60) status = 'available';      // 60% available
          else if (randomValue < 85) status = 'occupied';  // 25% occupied
          else if (randomValue < 95) status = 'reserved';  // 10% reserved
          else status = 'unavailable';                     // 5% unavailable

          // Generate section based on column position
          const section = dataCol < 10 ? 'A' : dataCol < 20 ? 'B' : dataCol < 30 ? 'C' : dataCol < 40 ? 'D' : 'E';
          
          grid.push({
            id: `${row}-${col}`,
            row,
            col,
            status,
            color: statusColors[status],
            slotId: `C-F${floor}-${String(row + 1).padStart(2, '0')}-${String(dataCol + 1).padStart(2, '0')}`,
            displayId: `M${floor}${section}${String(row + 1).padStart(2, '0')}${String(dataCol + 1).padStart(2, '0')}`,
            location: `Floor ${floor}, Section ${section}`,
            position: `Row ${row + 1}, Column ${dataCol + 1}`,
            dimensions: '30×30×30 cm',
            price: floor === 2 ? '₱40,000.00' : '₱50,000.00',
            type: 'Single'
          });
        }
      }
    }
    return grid;
  };

  const allGrid = generateGrid(currentFloor);
  
  // Filter grid based on status filter from parent
  const grid = statusFilter === 'all' 
    ? allGrid 
    : allGrid.filter(block => block.status === statusFilter);
  
  // Debug logging
  console.log('Status filter:', statusFilter);
  console.log('Total blocks:', allGrid.length);
  console.log('Filtered blocks:', grid.length);

  // Handle floor switching
  const handleFloorSwitch = (floor) => {
    setCurrentFloor(floor);
    // Reset zoom and pan when switching floors
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
    setIsInitialized(false);
    if (onFloorChange) {
      onFloorChange(floor);
    }
  };

  // Calculate minimum zoom level to fit all rows in viewport
  const getMinZoomLevel = useCallback(() => {
    const containerHeight = window.innerHeight - 100; // Account for header
    const gridHeight = 240; // Grid height (8 rows)
    
    // Calculate zoom level needed to fit all rows (height-based)
    // Use 90% of container height to ensure proper fit with padding
    const minZoomY = (containerHeight * 0.9) / gridHeight;
    
    return minZoomY;
  }, []);

  // Initialize zoom level to minimum on mount
  useEffect(() => {
    if (!isInitialized) {
      const minZoom = getMinZoomLevel();
      setZoomLevel(minZoom);
      setPanX(0); // Center horizontally
      setPanY(0); // Start at top
      setIsInitialized(true);
      console.log('Initialized columbarium with min zoom:', minZoom);
    }
  }, [isInitialized, getMinZoomLevel]);

  // Debug zoom level changes
  useEffect(() => {
    console.log('Zoom level changed to:', zoomLevel);
  }, [zoomLevel]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const newLevel = Math.min(prev * 1.2, 3);
      console.log('Zoom In:', prev, '->', newLevel);
      
      // Apply pan constraints after zoom
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight - 200; // Account for header and legend
      const gridWidth = 1580; // Grid width with spacing
      const gridHeight = 240; // Grid height (8 rows)
      const minZoom = getMinZoomLevel();
      
      if (newLevel <= minZoom + 0.01) {
        // At minimum zoom: center vertically, allow horizontal panning
        const maxPanX = Math.max(0, (gridWidth * newLevel - containerWidth) / 2);
        setPanX(prevPanX => Math.max(-maxPanX, Math.min(maxPanX, prevPanX)));
        setPanY(0);
      } else {
        // Above minimum zoom: allow both horizontal and vertical panning
        const maxPanX = Math.max(0, (gridWidth * newLevel - containerWidth) / 2);
        const maxPanY = Math.max(0, (gridHeight * newLevel - containerHeight) / 2);
        
        setPanX(prevPanX => Math.max(-maxPanX, Math.min(maxPanX, prevPanX)));
        setPanY(prevPanY => Math.max(-maxPanY, Math.min(maxPanY, prevPanY)));
      }
      
      return newLevel;
    });
  }, [getMinZoomLevel]);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const minZoom = getMinZoomLevel();
      const newLevel = Math.max(prev / 1.2, minZoom);
      console.log('Zoom Out:', prev, '->', newLevel, '(min:', minZoom, ')');
      
      // Apply pan constraints after zoom
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight - 200; // Account for header and legend
      const gridWidth = 1580; // Grid width with spacing
      const gridHeight = 240; // Grid height (8 rows)
      
      if (newLevel <= minZoom + 0.01) {
        // At minimum zoom: center vertically, allow horizontal panning
        const maxPanX = Math.max(0, (gridWidth * newLevel - containerWidth) / 2);
        setPanX(prevPanX => Math.max(-maxPanX, Math.min(maxPanX, prevPanX)));
        setPanY(0);
      } else {
        // Above minimum zoom: allow both horizontal and vertical panning
        const maxPanX = Math.max(0, (gridWidth * newLevel - containerWidth) / 2);
        const maxPanY = Math.max(0, (gridHeight * newLevel - containerHeight) / 2);
        
        setPanX(prevPanX => Math.max(-maxPanX, Math.min(maxPanX, prevPanX)));
        setPanY(prevPanY => Math.max(-maxPanY, Math.min(maxPanY, prevPanY)));
      }
      
      return newLevel;
    });
  }, [getMinZoomLevel]);

  const handleReset = useCallback(() => {
    const minZoom = getMinZoomLevel();
    setZoomLevel(minZoom);
    setPanX(0);
    setPanY(0);
    console.log('Reset to min zoom:', minZoom);
  }, [getMinZoomLevel]);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
  }, [panX, panY]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;
      
      // Calculate constraints based on zoom level and container size
        const containerWidth = window.innerWidth;
        const containerHeight = window.innerHeight - 200; // Account for header and legend
        const gridWidth = 1580; // Grid width with spacing
        const gridHeight = 240; // Grid height (8 rows)
        const minZoom = getMinZoomLevel();
        
        let constrainedPanX, constrainedPanY;
      
        if (zoomLevel <= minZoom + 0.01) {
          // At minimum zoom: only allow horizontal panning, center vertically
          const maxPanX = Math.max(0, (gridWidth * zoomLevel - containerWidth) / 2);
          constrainedPanX = Math.max(-maxPanX, Math.min(maxPanX, newPanX));
          constrainedPanY = 0; // Keep centered vertically
        } else {
          // Above minimum zoom: allow both horizontal and vertical panning
          const maxPanX = Math.max(0, (gridWidth * zoomLevel - containerWidth) / 2);
          const maxPanY = Math.max(0, (gridHeight * zoomLevel - containerHeight) / 2);
          
          constrainedPanX = Math.max(-maxPanX, Math.min(maxPanX, newPanX));
          constrainedPanY = Math.max(-maxPanY, Math.min(maxPanY, newPanY));
        }
      
      setPanX(constrainedPanX);
      setPanY(constrainedPanY);
    }
  }, [isDragging, dragStart, zoomLevel, getMinZoomLevel]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1; // Zoom out on scroll down, zoom in on scroll up
    setZoomLevel(prev => {
      const minZoom = getMinZoomLevel();
      const newLevel = Math.max(minZoom, Math.min(3, prev * delta));
      console.log('Mouse wheel zoom:', prev, '->', newLevel, '(min:', minZoom, ')');
      
      // Apply pan constraints after zoom
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight - 200; // Account for header and legend
      const gridWidth = 1580; // Grid width with spacing
      const gridHeight = 240; // Grid height (8 rows)
      
      if (newLevel <= minZoom + 0.01) {
        // At minimum zoom: center vertically, allow horizontal panning
        const maxPanX = Math.max(0, (gridWidth * newLevel - containerWidth) / 2);
        setPanX(prevPanX => Math.max(-maxPanX, Math.min(maxPanX, prevPanX)));
        setPanY(0);
      } else {
        // Above minimum zoom: allow both horizontal and vertical panning
        const maxPanX = Math.max(0, (gridWidth * newLevel - containerWidth) / 2);
        const maxPanY = Math.max(0, (gridHeight * newLevel - containerHeight) / 2);
        
        setPanX(prevPanX => Math.max(-maxPanX, Math.min(maxPanX, prevPanX)));
        setPanY(prevPanY => Math.max(-maxPanY, Math.min(maxPanY, prevPanY)));
      }
      
      return newLevel;
    });
  }, [getMinZoomLevel]);

  const handleBlockClick = useCallback((block, event) => {
    console.log('Clicked block:', block);
    
    // Don't show popup for spacer blocks
    if (block.status === 'spacer') return;
    
    // Calculate popup position relative to the clicked block
    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 280; // Approximate popup width
    const popupHeight = 200; // Approximate popup height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate initial position (center horizontally, above the block)
    let popupX = rect.left + rect.width / 2;
    let popupY = rect.top - popupHeight - 10; // Position above the block with popup height
    
    // Adjust horizontal position if popup would go off-screen
    if (popupX - popupWidth / 2 < 10) {
      popupX = popupWidth / 2 + 10; // Keep 10px margin from left edge
    } else if (popupX + popupWidth / 2 > viewportWidth - 10) {
      popupX = viewportWidth - popupWidth / 2 - 10; // Keep 10px margin from right edge
    }
    
    // For blocks in lower portion, move popup up more to avoid overflow
    const blockCenterY = rect.top + rect.height / 2;
    const viewportCenterY = viewportHeight / 2;
    
    // If block is in lower half of viewport, move popup up more
    if (blockCenterY > viewportCenterY) {
      popupY = rect.top - popupHeight - 100; // Move up 100px more for lower blocks
    }
    
    // If popup would go above viewport, position it below the block instead
    if (popupY < 10) {
      popupY = rect.bottom + 10; // Position below the block
    }
    
    // Ensure popup doesn't go below viewport
    if (popupY + popupHeight > viewportHeight - 10) {
      popupY = viewportHeight - popupHeight - 10;
    }
    
    setSelectedBlock(block);
    setPopupPosition({ x: popupX, y: popupY });
  }, []);

  const handleClosePopup = useCallback(() => {
    setSelectedBlock(null);
  }, []);

  const handleReserveBlock = useCallback((block) => {
    if (block.status === 'available') {
      // Store the selected slot data in localStorage for the columbarium page
      const slotData = {
        slotId: block.slotId,
        displayId: block.displayId,
        location: block.location,
        position: block.position,
        dimensions: block.dimensions,
        price: block.price,
        type: block.type,
        floor: currentFloor,
        row: block.row,
        column: block.col,
        section: block.section || 'A' // Default section if not set
      };
      
      localStorage.setItem('selectedColumbariumSlot', JSON.stringify(slotData));
      localStorage.setItem('columbariumReservationStep', '3'); // Skip to step 3
      
      // Redirect to the columbarium page for reservation
      window.location.href = '/client/columbarium';
    }
  }, [currentFloor]);

  const handleBookmarkBlock = useCallback((block) => {
    // Here you would typically make an API call to bookmark the block
    console.log('Bookmarking block:', block);
    
    // For now, we'll just show an alert
    alert(`Bookmarked block ${block.displayId}\nLocation: ${block.location}`);
    
    // Close the popup after bookmarking
    setSelectedBlock(null);
  }, []);


  return (
    <div className="columbarium-grid-container">
      {/* Header */}
      <div className="columbarium-header">
        <div className="columbarium-title">
          <div className="button-group">
            <button 
              className="back-button"
              onClick={onBackToMap}
              title="Back to Map"
            >
              ←
            </button>
            <button 
              className="control-btn zoom-out"
              onClick={() => {
                console.log('Zoom Out button clicked');
                handleZoomOut();
              }}
              title="Zoom Out"
            >
              −
            </button>
            <button 
              className="control-btn zoom-in"
              onClick={() => {
                console.log('Zoom In button clicked');
                handleZoomIn();
              }}
              title="Zoom In"
            >
              +
            </button>
            <button 
              className="control-btn reset"
              onClick={handleReset}
              title="Reset View"
            >
              ↻
            </button>
          </div>
           <div className="controls-right">
             <div className="dropdowns-row">
               <div className="floor-dropdown-container">
                 <select 
                   className="floor-dropdown"
                   value={currentFloor}
                   onChange={(e) => handleFloorSwitch(parseInt(e.target.value))}
                 >
                   <option value={1}>Floor 1</option>
                   <option value={2}>Floor 2</option>
                 </select>
               </div>
               <div className="filter-dropdown-container">
                 <select 
                   className="filter-dropdown"
                   value={statusFilter}
                   onChange={(e) => {
                     if (onFilterChange) {
                       onFilterChange(e.target.value);
                     }
                   }}
                 >
                   <option value="all">All Blocks</option>
                   <option value="available">Available</option>
                   <option value="reserved">Reserved</option>
                   <option value="occupied">Occupied</option>
                   <option value="unavailable">Unavailable</option>
                 </select>
               </div>
               <div className="columbarium-count">
                 Showing {grid.length} of 400 blocks
               </div>
             </div>
           </div>
        </div>
      </div>

      {/* Grid Container */}
      <div 
        className="columbarium-grid-wrapper"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div 
          className="columbarium-grid"
          style={{
            transform: `translate(${panX}px, ${panY}px) scale(${zoomLevel})`,
            transformOrigin: 'center center'
          }}
        >
          {/* Section outlines */}
          <div className="section-outline section-a"></div>
          <div className="section-outline section-b"></div>
          <div className="section-outline section-c"></div>
          <div className="section-outline section-d"></div>
          <div className="section-outline section-e"></div>
          
          {grid.map((block) => (
            <div
              key={block.id}
              className={`columbarium-block ${block.status}`}
              style={{
                gridColumn: block.col + 1,
                gridRow: block.row + 1
              }}
              onClick={(e) => handleBlockClick(block, e)}
              title={`Slot ${block.slotId} - ${block.status}`}
            />
          ))}
        </div>
      </div>

      {/* Block Info Popup */}
      {selectedBlock && (
        <div 
          className="block-popup"
          style={{
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="popup-header">
            <div className="popup-id">{selectedBlock.displayId}</div>
            <div className="popup-type">{selectedBlock.type}</div>
          </div>
          
          <div className="popup-info">
            <div className="info-row">
              <span className="info-label">Location:</span>
              <span className="info-value">{selectedBlock.location}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Position:</span>
              <span className="info-value">{selectedBlock.position}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Dimensions:</span>
              <span className="info-value">{selectedBlock.dimensions}</span>
            </div>
          </div>
          
          <div className="popup-price">{selectedBlock.price}</div>
          
          <div className="popup-actions">
            <button 
              className="action-btn bookmark-btn"
              onClick={() => handleBookmarkBlock(selectedBlock)}
            >
              BOOKMARK
            </button>
            {selectedBlock.status === 'available' && (
              <button 
                className="action-btn reserve-now"
                onClick={() => handleReserveBlock(selectedBlock)}
              >
                RESERVE NOW
              </button>
            )}
            <button className="action-btn cancel-btn" onClick={handleClosePopup}>CANCEL</button>
          </div>
        </div>
      )}


    </div>
  );
};

export default ColumbariumGrid;
